﻿import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

import styles from './NotificationModal.module.scss';
import Cross from 'assets/icons/close-button-small.svg?react';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import ChevronDown from 'assets/icons/arrow-language-selector.svg?react';
import animationData from 'assets/animations/loader_small.json';
import i18n from 'i18next';
import {
  NotificationType,
  UserClient,
  UserQuery,
} from 'services/api/api-client.ts';
import {
  AllowedRoles,
  NotificationAllowedPages,
  all,
} from '../../../application/constants/allowed-roles.ts';
import { Checkbox } from '../../../components/uikit/inputs/Checkbox.tsx';
import { NotificationsPlaceholder } from './NotificationsPlaceholder.tsx';
import { tDynamic } from '../../../helpers/tDynamic.ts';
import InfiniteScroll from 'react-infinite-scroll-component';
import { LottieContainer } from '../../../components/animations/LottieContainer.tsx';
import DOMPurify from 'dompurify';
import { formatDetailedDate } from '../../../helpers/abas-helper.ts';
import { Trans } from 'react-i18next';

const formatDate = (dateInput: Date) => {
  const date = new Date(dateInput);
  const now = new Date();

  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = diffMs / 1000;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffSeconds < 60) {
    return i18n.t('Page.NotificationModal.now');
  }

  if (diffHours < 1) {
    return `${diffMinutes} ${i18n.t('Page.NotificationModal.minAgo')}`;
  }

  if (diffDays < 1) {
    return `${diffHours} ${i18n.t('Page.NotificationModal.hourAgo')}`;
  }

  if (diffDays < 2) {
    return i18n.t('Page.NotificationModal.yesterday');
  }

  return date.toLocaleDateString();
};

export interface Notification {
  id: number;
  title: string;
  text: string;
  notificationParams?: { [key: string]: any } | null;
  url: string | null;
  type: NotificationType;
  createdDate: Date;
}

interface NotificationsModalProps {
  notifications: Notification[];
  loadMore: () => void;
  hasMore: boolean;
  onClose: () => void;
  onNotificationClick: (notification: Notification) => void;
  isErrorHappened: boolean;
  selectedTypes: NotificationType[];
  onSelectedTypesChange: (types: NotificationType[]) => void;
}

export const NotificationsModal = ({
  notifications,
  loadMore,
  hasMore,
  onClose,
  onNotificationClick,
  isErrorHappened,
  selectedTypes,
  onSelectedTypesChange,
}: NotificationsModalProps) => {
  const i18n = useScopedTranslation('Page.NotificationModal');
  const userQuery = UserQuery.useGetCurrentUserInfoQuery({
    staleTime: 30000,
  });
  const [isSelectVisible, setIsSelectVisible] = useState(false);
  const userRoles = userQuery.data?.claimRoles ?? [];
  const notificationTypes = Object.values(NotificationType).filter(
    (notificationType) => {
      if (
        notificationType === NotificationType.Bucharest ||
        notificationType === NotificationType.WarrantyRequests
      ) {
        return false;
      }

      const pageName = NotificationAllowedPages[notificationType];
      const allowedRoles = pageName ? AllowedRoles[pageName] : all;

      return (
        allowedRoles === all ||
        allowedRoles.some((role) => userRoles.includes(role))
      );
    },
  );
  const allNotificationTypes = [
    NotificationType.Bucharest,
    ...notificationTypes,
  ];

  const lastReadDate = new Date(
    userQuery.data?.options?.lastReadNotificationDate ?? 0,
  );
  const currentLastReadDate =
    userQuery.data?.options?.lastReadNotificationDate ?? 0;

  const markLastReadDate = async () => {
    const newLastReadDate = notifications.at(0)?.createdDate?.getTime();

    if (!newLastReadDate || currentLastReadDate >= newLastReadDate) {
      return;
    }

    const isFirmwareNotificationsDisabled =
      userQuery.data?.options?.isFirmwareNotificationsDisabled ?? null;
    const isQualificationNotificationsDisabled =
      userQuery.data?.options?.isQualificationNotificationsDisabled ?? null;
    const isRepairsNotificationsDisabled =
      userQuery.data?.options?.isRepairsNotificationsDisabled ?? null;

    await UserClient.updateCurrentUserInfo({
      options: {
        lastReadNotificationDate: newLastReadDate,
        isFirmwareNotificationsDisabled,
        isQualificationNotificationsDisabled,
        isRepairsNotificationsDisabled,
      },
    });

    await userQuery.refetch();
  };

  useEffect(() => {
    const onBeforeUnload = () => {
      void markLastReadDate();
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  const handleClose = () => {
    void markLastReadDate();
    onClose();
  };

  const getSelectTitle = () => {
    if (
      !selectedTypes.length ||
      allNotificationTypes.every((notificationType) =>
        selectedTypes.includes(notificationType),
      )
    ) {
      return i18n.t('all_notifications');
    }

    const firstSelectedType = selectedTypes.at(0);
    let base = tDynamic(
      i18n,
      firstSelectedType === NotificationType.Bucharest
        ? 'General'
        : (firstSelectedType ?? ''),
    );

    if (selectedTypes.length > 1) {
      base = base + ` +${selectedTypes.length - 1}`;
    }

    return base;
  };

  const isAllChecked = () =>
    allNotificationTypes.every((notificationType) =>
      selectedTypes.includes(notificationType),
    );

  const isNotificationUnread = (createdAt: Date) => {
    return createdAt.getTime() > lastReadDate.getTime();
  };

  const getBucharestNotificationTitle = (notification: Notification): string =>
    String(
      (i18n.t as any)(notification.title, {
        defaultValue: DOMPurify.sanitize(notification.title),
        ...(notification.notificationParams ?? {}),
      }),
    );

  const getBucharestNotificationText = (notification: Notification): string => {
    const params = { ...(notification.notificationParams ?? {}) };

    return String(
      (i18n.t as any)(notification.text, {
        defaultValue: DOMPurify.sanitize(notification.text),
        ...params,
      }),
    );
  };

  return (
    <div className={styles.notificationsOverlay} onClick={handleClose}>
      <aside
        data-test-id="notification-modal"
        className={styles.panel}
        onClick={(e) => {
          e.stopPropagation();
          setIsSelectVisible(false);
        }}
      >
        <header className={styles.header}>
          <div className={styles.title}>{i18n.t('notifications')}</div>
          <IconButton
            className={styles.closeButton}
            iconClassName={styles.imageContainer}
            testId="notification-close"
            type={'destroy'}
            onClick={handleClose}
          >
            <Cross className={styles.image} />
          </IconButton>
        </header>
        <div className={styles.toolbar}>
          <div
            className={styles.select}
            onClick={(e) => {
              e.stopPropagation();
              setIsSelectVisible((prev) => !prev);
            }}
          >
            <ChevronDown
              className={
                isSelectVisible ? styles.chevronUp : styles.chevronDown
              }
            />
            <div
              className={clsx(
                styles.selectTitle,
                isSelectVisible && styles.openedSelectTitle,
              )}
            >
              {getSelectTitle()}
            </div>
            {isSelectVisible && (
              <div
                className={styles.selectContainer}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className={clsx(
                    styles.selectItem,
                    styles.selectItemBorderBottom,
                  )}
                >
                  <Checkbox
                    actionText={i18n.t('all')}
                    isChecked={isAllChecked()}
                    onChange={() => {
                      if (isAllChecked()) {
                        onSelectedTypesChange([]);
                      } else {
                        onSelectedTypesChange(allNotificationTypes);
                      }
                    }}
                    halfChecked={!!selectedTypes.length}
                  />
                </div>
                <div
                  className={clsx(
                    styles.selectItem,
                    selectedTypes.includes(NotificationType.Bucharest) &&
                      styles.selectedItem,
                  )}
                >
                  <Checkbox
                    actionText={tDynamic(i18n, 'General')}
                    isChecked={selectedTypes.includes(
                      NotificationType.Bucharest,
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onSelectedTypesChange([
                          ...selectedTypes,
                          NotificationType.Bucharest,
                        ]);
                      } else {
                        onSelectedTypesChange(
                          selectedTypes.filter(
                            (item) => item !== NotificationType.Bucharest,
                          ),
                        );
                      }
                    }}
                  />
                </div>
                {notificationTypes.map((notificationType) => (
                  <div
                    className={clsx(
                      styles.selectItem,
                      selectedTypes.includes(notificationType) &&
                        styles.selectedItem,
                    )}
                    key={notificationType}
                  >
                    <Checkbox
                      actionText={tDynamic(i18n, notificationType)}
                      isChecked={selectedTypes.includes(notificationType)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onSelectedTypesChange([
                            ...selectedTypes,
                            notificationType,
                          ]);
                        } else {
                          onSelectedTypesChange(
                            selectedTypes.filter(
                              (item) => item !== notificationType,
                            ),
                          );
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <>
          {!notifications.length ? (
            <NotificationsPlaceholder />
          ) : (
            <div id="scrollableDiv" className={styles.list}>
              <InfiniteScroll
                dataLength={notifications.length}
                next={loadMore}
                hasMore={hasMore}
                loader={
                  <div className={styles.loading}>
                    <LottieContainer
                      animationData={animationData}
                      className={styles.lottie}
                    />
                  </div>
                }
                scrollableTarget="scrollableDiv"
              >
                {notifications.map((n) => {
                  const bucharestNotificationTitle =
                    getBucharestNotificationTitle(n);
                  const bucharestNotificationText =
                    getBucharestNotificationText(n);

                  const notificationParams = n.notificationParams;

                  if (
                    n.type === NotificationType.Qualifications &&
                    notificationParams?.expiryDate
                  ) {
                    const formattedExpiryDate = formatDetailedDate(
                      notificationParams.expiryDate,
                      i18n.i18n,
                      false,
                    );
                    notificationParams.formattedExpiryDate =
                      formattedExpiryDate ??
                      String(notificationParams.expiryDate);
                  }

                  const isBucharestNotification =
                    n.type === NotificationType.Bucharest;

                  return (
                    <div
                      key={n.id}
                      data-test-id="notification-item"
                      className={clsx(
                        styles.item,
                        isErrorHappened && styles.errorMessage,
                        n.url && styles.itemHover,
                      )}
                      onClick={() => {
                        if (n.url) {
                          void markLastReadDate();
                        }
                        onNotificationClick(n);
                      }}
                    >
                      <div className={styles.content}>
                        <div
                          className={clsx(
                            styles.itemTitle,
                            isNotificationUnread(n.createdDate) &&
                              styles.newItem,
                          )}
                        >
                          <span
                            className={styles.disableDefaultMargins}
                            dangerouslySetInnerHTML={{
                              __html: bucharestNotificationTitle,
                            }}
                          />
                        </div>
                        <div className={styles.itemText}>
                          {isBucharestNotification ? (
                            <span
                              className={styles.disableDefaultMargins}
                              dangerouslySetInnerHTML={{
                                __html: bucharestNotificationText,
                              }}
                            />
                          ) : (
                            <Trans
                              i18nKey={
                                `Page.NotificationModal.${n.text}` as any
                              }
                              defaultValue={n.text}
                              values={notificationParams ?? {}}
                              components={{
                                bold: (
                                  <strong className={styles.fontWeight600} />
                                ),
                              }}
                            />
                          )}
                        </div>
                      </div>

                      <div
                        className={clsx(
                          styles.itemText,
                          isNotificationUnread(n.createdDate) && styles.newItem,
                          styles.noWrap,
                        )}
                        data-test-id="notification-date"
                      >
                        {formatDate(n.createdDate)}
                      </div>
                    </div>
                  );
                })}
              </InfiniteScroll>
            </div>
          )}
        </>
      </aside>
    </div>
  );
};
